import theme from '@adsk/alloy-react-theme';

import {
  AlloyLegacyThemeDesignTokens,
  TreeDesignTokens,
  WeaveTheme,
} from '../types';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

const ROW_BASE = {
  display: 'flex',
  'flex-flow': 'row nowrap',
  'align-items': 'center',
  'user-select': 'none',
  width: '100%',
  height: '40px',
  overflow: 'hidden',
} as const;

export const getTreeTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): TreeDesignTokens => {
  const { tokens, shadows, borderRadius, zIndex } = theme;
  const { colors } = tokens;

  return {
    container: {
      _: {
        padding: 0,
        'background-color': colors.background.primary.value,
      },
    },
    node: {
      _: {
        ...ROW_BASE,
      },
      state: {
        hover: {
          'background-color': colors.background.neutral.hover.value,
        },
        pressed: {
          'background-color': colors.background.neutral.pressed.value,
        },
        selected: {
          'background-color': colors.background.selected.subtler.value,
          '&:hover': {
            'background-color': colors.background.selected.subtler.value,
          },
        },
        disabled: {
          color: colors.text.inactive.value,
        },
      },
      rowClick: {
        true: { cursor: 'pointer' },
        false: { cursor: 'default' },
      },
    },
    indent: {
      _: {},
      guideline: {
        'border-width': '1px',
        'border-style': 'dashed',
        'border-color': colors.border.value,
        opacity: 0.5,
      },
    },
    expandButton: {
      _: {},
      icon: {
        color: colors.icon.secondary.value,
      },
    },
    label: {
      _: {},
    },
    dropdown: {
      container: {
        'box-shadow': shadows.highElevation,
        'background-color': colors.background.primary.value,
        'max-width': 'calc(100% + 70px)',
        'border-radius': `${borderRadius.medium}px`,
      },
      inner: {
        'border-radius': `${borderRadius.medium}px`,
        padding: 0,
        margin: 0,
        outline: 'none',
        'overflow-y': 'auto',
        'z-index': zIndex.tooltip,
      },
    },
  };
};

export const getTreeTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): TreeDesignTokens => {
  const treeitem = tokens?.component?.treeitem;
  const popover = tokens?.component?.popover;
  const typography = tokens?.component?.typography;
  const generic = tokens?.generic;
  const semantic = tokens?.semantic;

  const bg = treeitem?.['background-color'];
  const textColor = typography?.['text-color'];
  const disabledText = generic?.color?.charcoal?.[500]?.[100];

  const shadowX = popover?.['box-shadow-x'];
  const shadowY = popover?.['box-shadow-y'];
  const shadowBlur = popover?.['box-shadow-blur'];
  const shadowColor = popover?.['box-shadow-color'];
  const popoverShadow =
    shadowX !== undefined &&
    shadowY !== undefined &&
    shadowBlur !== undefined &&
    shadowColor !== undefined
      ? `${shadowX} ${shadowY} ${shadowBlur} ${shadowColor}`
      : undefined;

  return {
    container: {
      _: {
        padding: 0,
        'background-color': popover?.['background-color'],
      },
    },
    node: {
      _: {
        ...ROW_BASE,
        'font-family': fontFamily,
        'font-size': treeitem?.['font-size'],
        'line-height': treeitem?.['line-height'],
        color: textColor,
      },
      state: {
        hover: {
          'background-color': bg?.hover,
        },
        pressed: {
          'background-color':
            semantic?.['background-color']?.ghost?.high?.pressed,
        },
        selected: {
          'background-color': bg?.selected?.default,
          '&:hover': {
            'background-color': bg?.selected?.hover,
          },
        },
        disabled: {
          color: disabledText,
        },
      },
      rowClick: {
        true: { cursor: 'pointer' },
        false: { cursor: 'default' },
      },
    },
    indent: {
      _: {},
      guideline: {
        'border-width': treeitem?.guideline?.['border-width'],
        'border-style': treeitem?.guideline?.['border-style']?.default,
        'border-color': treeitem?.guideline?.['border-color']?.default,
        opacity: 1,
      },
    },
    expandButton: {
      _: {},
      icon: {
        color: semantic?.['icon-color']?.medium,
      },
    },
    label: {
      _: {},
    },
    dropdown: {
      container: {
        'box-shadow': popoverShadow,
        'background-color': popover?.['background-color'],
        'max-width': 'calc(100% + 70px)',
        'border-radius': popover?.['border-radius'],
      },
      inner: {
        'border-radius': popover?.['border-radius'],
        padding: 0,
        margin: 0,
        outline: 'none',
        'overflow-y': 'auto',
        'z-index': theme.zIndex.tooltip,
      },
    },
  };
};
